import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
import { Table, Card, Form, Row, Col, Button, InputGroup, FormControl, Badge } from 'react-bootstrap';
import reportService from "../services/report.service";
import { FaChevronDown, FaChevronUp, FaSort, FaSortUp, FaSortDown, FaDatabase, FaUser, FaCalendarAlt, FaHdd, FaSearch } from 'react-icons/fa';

// Helper to compute date range
const getFirstDayOfLastMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const getLastDayOfLastMonth = () => {
    const d = new Date();
    d.setDate(1); // First day of this month
    d.setDate(0); // Last day of last month
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const DataUsage = () => {
    const { user: currentUser } = useSelector((state) => state.auth);
    
    // State variables
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchInitiated, setSearchInitiated] = useState(false);
    
    // Date Pickers
    const [startDate, setStartDate] = useState(getFirstDayOfLastMonth());
    const [endDate, setEndDate] = useState(getLastDayOfLastMonth());
    const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");

    // Search and Pagination States
    const [searchName, setSearchName] = useState("");
    const [page, setPage] = useState(0);
    const pageSize = 50;

    // Sorting states
    const [orderBy, setOrderBy] = useState("totalSizeMb");
    const [sortDirection, setSortDirection] = useState("DESC");
    
    // Nested user sorting states
    const [userSortField, setUserSortField] = useState("sizeMb");
    const [userSortDirection, setUserSortDirection] = useState("DESC");

    // Tracks which client rows are expanded
    const [expandedRows, setExpandedRows] = useState({});

    // Date picker validation
    const isDateRangeInvalid = startDate && endDate && new Date(endDate) < new Date(startDate);

    const handleStartDateChange = (val) => {
        setStartDate(val);
        if (val) {
            const parts = val.split('-');
            if (parts.length === 3) {
                const year = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10);
                const lastDay = new Date(year, month, 0);
                const yyyy = lastDay.getFullYear();
                const mm = String(lastDay.getMonth() + 1).padStart(2, '0');
                const dd = String(lastDay.getDate()).padStart(2, '0');
                setEndDate(`${yyyy}-${mm}-${dd}`);
            }
        }
    };

    const handleRunReport = (e) => {
        if (e) e.preventDefault();
        
        if (!startDate || !endDate) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please select both start and end dates." });
            return;
        }

        if (isDateRangeInvalid) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Validation Error: End date must be greater than or equal to start date." });
            return;
        }

        setLoading(true);
        setSearchInitiated(true);
        setPage(0);
        setExpandedRows({});
        
        reportService.getDataUsage(startDate, endDate, timezone, searchName).then(
            (response) => {
                setData(response.data || []);
                setLoading(false);
            },
            (error) => {
                const errorMessage = (error.response && error.response.data) || error.message || error.toString();
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to fetch data usage report: " + errorMessage });
                setLoading(false);
            }
        );
    };

    const handleClearReport = () => {
        setStartDate(getFirstDayOfLastMonth());
        setEndDate(getLastDayOfLastMonth());
        setData([]);
        setSearchInitiated(false);
        setExpandedRows({});
        setSearchName("");
        setPage(0);
    };

    const toggleRow = (clientId) => {
        setExpandedRows(prev => ({
            ...prev,
            [clientId]: !prev[clientId]
        }));
    };

    const handleSort = (field) => {
        if (orderBy === field) {
            setSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
        } else {
            setOrderBy(field);
            setSortDirection("ASC");
        }
        setPage(0);
    };

    const handleUserSort = (field) => {
        if (userSortField === field) {
            setUserSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
        } else {
            setUserSortField(field);
            setUserSortDirection("ASC");
        }
    };

    const renderSortIcon = (field) => {
        if (orderBy !== field) {
            return <FaSort className="ms-1 text-white-50" style={{ opacity: 0.6 }} />;
        }
        return sortDirection === "ASC" 
            ? <FaSortUp className="ms-1 text-warning" />
            : <FaSortDown className="ms-1 text-warning" />;
    };

    const renderUserSortIcon = (field) => {
        if (userSortField !== field) {
            return <FaSort className="ms-1 text-white-50" style={{ opacity: 0.6 }} />;
        }
        return userSortDirection === "ASC" 
            ? <FaSortUp className="ms-1 text-warning" />
            : <FaSortDown className="ms-1 text-warning" />;
    };

    const getSortedClients = () => {
        if (!data) return [];
        return [...data].sort((a, b) => {
            let valA = a[orderBy];
            let valB = b[orderBy];
            
            if (valA === undefined || valA === null) valA = "";
            if (valB === undefined || valB === null) valB = "";
            
            if (orderBy === "totalSizeMb" || orderBy === "totalFileCount" || orderBy === "totalRoCount") {
                return sortDirection === "ASC" ? valA - valB : valB - valA;
            }
            
            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();
            
            if (strA < strB) return sortDirection === "ASC" ? -1 : 1;
            if (strA > strB) return sortDirection === "ASC" ? 1 : -1;
            return 0;
        });
    };

    const getSortedUsers = (users) => {
        if (!users) return [];
        return [...users].sort((a, b) => {
            let valA = a[userSortField];
            let valB = b[userSortField];
            
            if (valA === undefined || valA === null) valA = "";
            if (valB === undefined || valB === null) valB = "";
            
            if (userSortField === "sizeMb" || userSortField === "fileCount" || userSortField === "roCount") {
                return userSortDirection === "ASC" ? valA - valB : valB - valA;
            }
            
            const strA = String(valA).toLowerCase();
            const strB = String(valB).toLowerCase();
            
            if (strA < strB) return userSortDirection === "ASC" ? -1 : 1;
            if (strA > strB) return userSortDirection === "ASC" ? 1 : -1;
            return 0;
        });
    };

    const handleNextPage = () => {
        const totalPages = Math.ceil(totalCount / pageSize);
        if (page + 1 < totalPages) {
            setPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(prev => prev - 1);
        }
    };

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return (
            <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
                <div className="alert alert-danger">
                    <h4>Unauthorized Access</h4>
                    <p>You must be an administrator to view this page.</p>
                </div>
            </div>
        );
    }

    const sortedClients = getSortedClients();
    const totalCount = sortedClients.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const paginatedClients = sortedClients.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <style>{`
                .sortable-device-header {
                    transition: color 0.15s ease-in-out;
                }
                .sortable-device-header:hover {
                    color: #ffc107 !important; /* bootstrap warning yellow */
                }
            `}</style>
            <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center py-3" style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <div className="d-flex align-items-center">
                        <FaDatabase size={22} className="me-2 text-warning" />
                        <h4 className="mb-0 fw-semibold">Upload Audit Data Usage</h4>
                    </div>
                    <Badge bg="secondary" className="px-3 py-2 fs-6">
                        Active Timezone: {timezone}
                    </Badge>
                </Card.Header>
                <Card.Body className="p-4">
                    
                    {/* Date Picker + Search Filter Panel */}
                    <Form onSubmit={handleRunReport} className="mb-4">
                        <Row className="g-3 align-items-end">
                            <Col lg={3} md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-muted mb-2">
                                        <FaCalendarAlt className="me-1" /> Start Date
                                    </Form.Label>
                                    <Form.Control 
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => handleStartDateChange(e.target.value)}
                                        disabled={loading}
                                    />
                                </Form.Group>
                            </Col>

                            <Col lg={3} md={6}>
                                <Form.Group className="position-relative">
                                    <Form.Label className="fw-semibold text-muted mb-2">
                                        <FaCalendarAlt className="me-1" /> End Date
                                    </Form.Label>
                                    <Form.Control 
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        disabled={loading}
                                        isInvalid={isDateRangeInvalid}
                                    />
                                    <Form.Control.Feedback type="invalid" className="position-absolute" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                        End date must be >= start date.
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col lg={4} md={8}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold text-muted mb-2">
                                        <FaSearch className="me-1" /> Client Name / ID (optional)
                                    </Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-white border-end-0">
                                            <FaSearch className="text-muted" />
                                        </InputGroup.Text>
                                        <FormControl 
                                            placeholder="Filter by client name or ID..."
                                            value={searchName}
                                            onChange={(e) => setSearchName(e.target.value)}
                                            disabled={loading}
                                            className="border-start-0 ps-0"
                                        />
                                        {searchName && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setSearchName("")}
                                                disabled={loading}
                                            >
                                                ✕
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Form.Group>
                            </Col>

                            <Col lg={2} className="d-flex justify-content-lg-end justify-content-start mt-3 mt-lg-0">
                                <Button 
                                    type="submit" 
                                    variant="primary" 
                                    size="md"
                                    disabled={loading || isDateRangeInvalid}
                                    className="me-2 px-3 fw-semibold"
                                >
                                    {loading ? "Running..." : "Run"}
                                </Button>
                                <Button 
                                    variant="outline-secondary" 
                                    size="md"
                                    onClick={handleClearReport}
                                    disabled={loading}
                                    className="px-3 fw-semibold"
                                >
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    {/* Mini Pagination (Only visible when report loaded) */}
                    {searchInitiated && !loading && data.length > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mb-3 pt-3 border-top">
                            <span className="text-muted fs-6">
                                Showing <strong>{paginatedClients.length}</strong> of <strong>{totalCount}</strong> clients
                            </span>
                            <div className="d-flex align-items-center">
                                <span className="me-3 text-muted fs-6">
                                    Page <strong>{page + 1}</strong> of {Math.max(1, totalPages)}
                                </span>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="me-2 px-3 fw-semibold" 
                                    onClick={handlePrevPage} 
                                    disabled={page === 0}
                                >
                                    Previous
                                </Button>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="px-3 fw-semibold" 
                                    onClick={handleNextPage} 
                                    disabled={page + 1 >= totalPages}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Master Table */}
                    <div className="table-responsive border rounded-3 overflow-hidden">
                        <Table hover className="mb-0 align-middle">
                            <thead className="table-dark">
                                <tr>
                                    <th style={{ width: '40px' }}></th>
                                    <th 
                                        onClick={() => handleSort("clientName")} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        className="py-3 px-4 fw-semibold text-white sortable-device-header"
                                    >
                                        Client Name / ID {renderSortIcon("clientName")}
                                    </th>
                                    <th 
                                        onClick={() => handleSort("totalSizeMb")} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        className="py-3 px-3 fw-semibold text-white text-center sortable-device-header"
                                    >
                                        Total Size (MB) {renderSortIcon("totalSizeMb")}
                                    </th>
                                    <th 
                                        onClick={() => handleSort("totalFileCount")} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        className="py-3 px-3 fw-semibold text-white text-center sortable-device-header"
                                    >
                                        Total Files {renderSortIcon("totalFileCount")}
                                    </th>
                                    <th 
                                        onClick={() => handleSort("totalRoCount")} 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        className="py-3 px-3 fw-semibold text-white text-center sortable-device-header"
                                    >
                                        Total ROs {renderSortIcon("totalRoCount")}
                                    </th>
                                    <th className="py-3 px-4 fw-semibold text-white text-end">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!searchInitiated ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted fw-semibold">
                                            <FaHdd size={42} className="mb-3 text-secondary d-block mx-auto" />
                                            <h5 className="text-dark fw-bold">Ready to Load Data Usage Report</h5>
                                            <p className="mb-0 text-muted fs-6">Select a date range above and click <strong>Run</strong> to load data usage statistics.</p>
                                        </td>
                                    </tr>
                                ) : loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5">
                                            <div className="spinner-border text-primary me-2" role="status"></div>
                                            <span className="text-muted fw-semibold fs-5">Processing upload audit logs...</span>
                                        </td>
                                    </tr>
                                ) : paginatedClients.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-5 text-muted fw-semibold">
                                            No data uploads recorded for the selected period/filter.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedClients.map((client) => {
                                        const isExpanded = !!expandedRows[client.clientId];
                                        return (
                                            <React.Fragment key={client.clientId}>
                                                <tr 
                                                    onClick={() => toggleRow(client.clientId)} 
                                                    style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                                                    className={isExpanded ? "table-active" : ""}
                                                >
                                                    <td className="text-center">
                                                        {isExpanded 
                                                            ? <FaChevronUp className="text-primary" /> 
                                                            : <FaChevronDown className="text-muted" />
                                                        }
                                                    </td>
                                                    <td className="px-4 fw-semibold text-dark fs-5">
                                                        <div>{client.clientName}</div>
                                                        <small className="text-muted monospace fw-normal" style={{ fontSize: '0.78rem', marginTop: '2px', display: 'block' }}>
                                                            ID: {client.clientId}
                                                        </small>
                                                    </td>
                                                    <td className="px-3 text-center">
                                                        <Badge bg={client.totalSizeMb > 0 ? "success" : "light"} className={client.totalSizeMb > 0 ? "px-3 py-2 fs-6 text-white" : "px-3 py-2 fs-6 text-dark border"}>
                                                            {client.totalSizeMb.toFixed(2)} MB
                                                        </Badge>
                                                    </td>
                                                    <td className="px-3 text-center text-dark fw-medium fs-6">
                                                        {client.totalFileCount || 0}
                                                    </td>
                                                    <td className="px-3 text-center text-dark fw-medium fs-6">
                                                        {client.totalRoCount || 0}
                                                    </td>
                                                    <td className="px-4 text-end">
                                                        <Button 
                                                            variant={isExpanded ? "primary" : "outline-secondary"}
                                                            size="sm"
                                                            className="fw-semibold px-3"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRow(client.clientId);
                                                            }}
                                                        >
                                                            {isExpanded ? "Hide" : "Show Users"}
                                                        </Button>
                                                    </td>
                                                </tr>

                                                {/* Expanded Details Row */}
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="6" className="bg-light p-4 border-top">
                                                            <div className="ps-4 pe-2 py-2">
                                                                 <h5 className="mb-3 text-dark fw-semibold d-flex align-items-center">
                                                                    <FaUser size={16} className="me-2 text-primary" />
                                                                    User Upload Statistics
                                                                 </h5>
                                                                
                                                                 {client.users.length === 0 ? (
                                                                     <div className="alert alert-warning border-0 mb-0 py-3 rounded-3 shadow-xs">
                                                                         No user upload records found.
                                                                     </div>
                                                                 ) : (
                                                                     <div className="table-responsive border rounded-3 overflow-hidden shadow-sm bg-white">
                                                                         <Table hover size="sm" className="mb-0 align-middle">
                                                                             <thead className="table-dark">
                                                                                 <tr>
                                                                                     <th 
                                                                                         onClick={() => handleUserSort("userName")} 
                                                                                         style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                         className="py-2 px-3 fw-medium text-white sortable-device-header"
                                                                                     >
                                                                                         Portal User {renderUserSortIcon("userName")}
                                                                                     </th>
                                                                                     <th 
                                                                                         onClick={() => handleUserSort("sizeMb")} 
                                                                                         style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                         className="py-2 px-3 fw-medium text-white sortable-device-header"
                                                                                     >
                                                                                         Upload Size (MB) {renderUserSortIcon("sizeMb")}
                                                                                     </th>
                                                                                     <th 
                                                                                         onClick={() => handleUserSort("fileCount")} 
                                                                                         style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                         className="py-2 px-3 fw-medium text-white sortable-device-header text-center"
                                                                                     >
                                                                                         Files Count {renderUserSortIcon("fileCount")}
                                                                                     </th>
                                                                                     <th 
                                                                                         onClick={() => handleUserSort("roCount")} 
                                                                                         style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                                         className="py-2 px-3 fw-medium text-white sortable-device-header text-center"
                                                                                     >
                                                                                         ROs Count {renderUserSortIcon("roCount")}
                                                                                     </th>
                                                                                 </tr>
                                                                             </thead>
                                                                             <tbody>
                                                                                 {getSortedUsers(client.users).map((user, idx) => (
                                                                                     <tr key={idx}>
                                                                                         <td className="px-3 py-3 fw-semibold text-dark">
                                                                                            <span className="d-flex align-items-center">
                                                                                                <FaUser className="text-secondary me-2" size={12} />
                                                                                                {user.userName}
                                                                                            </span>
                                                                                         </td>
                                                                                         <td className="px-3 py-3 text-muted monospace font-weight-bold">
                                                                                             {user.sizeMb.toFixed(2)} MB
                                                                                         </td>
                                                                                         <td className="px-3 py-3 text-muted monospace text-center">
                                                                                             {user.fileCount || 0}
                                                                                         </td>
                                                                                         <td className="px-3 py-3 text-muted monospace text-center">
                                                                                             {user.roCount || 0}
                                                                                         </td>
                                                                                     </tr>
                                                                                 ))}
                                                                             </tbody>
                                                                         </Table>
                                                                     </div>
                                                                 )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination display footer */}
                    {searchInitiated && !loading && totalCount > 0 && (
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-4">
                            <span className="text-muted mb-3 mb-sm-0">
                                Showing <strong>{paginatedClients.length}</strong> of <strong>{totalCount}</strong> clients
                            </span>
                            {totalPages > 1 && (
                                <div className="d-flex align-items-center">
                                    <Button 
                                        variant="outline-secondary" 
                                        className="me-2 fw-semibold"
                                        onClick={() => setPage(0)}
                                        disabled={page === 0}
                                    >
                                        First
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        className="me-2 fw-semibold"
                                        onClick={handlePrevPage} 
                                        disabled={page === 0}
                                    >
                                        Prev
                                    </Button>
                                    <span className="mx-2 font-weight-bold">
                                        Page <strong>{page + 1}</strong> of {totalPages}
                                    </span>
                                    <Button 
                                        variant="outline-secondary" 
                                        className="ms-2 me-2 fw-semibold"
                                        onClick={handleNextPage} 
                                        disabled={page + 1 >= totalPages}
                                    >
                                        Next
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        className="fw-semibold"
                                        onClick={() => setPage(totalPages - 1)}
                                        disabled={page + 1 >= totalPages}
                                    >
                                        Last
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                </Card.Body>
            </Card>
        </div>
    );
};

export default DataUsage;
